/**
 * F11 · Sunday looks different.
 *
 * *"Quieter digest, non-urgent nudges hold until Monday, the tablet shows the week
 * ahead rather than today's tasks."*
 *
 * Three halves of that sentence and this repo owns two of them. The digest is
 * composed in `fabric-api` (`POST /digest/compose`) and is not reachable from a
 * client; the README records that as the piece still outstanding. What the Front
 * Door can do it does: the rail holds what can wait, the ordering leads with the
 * week, and the kitchen tablet swaps the week and the task card round.
 *
 * ## Holding is not hiding, and the difference is one sentence
 *
 * The estate's cardinal sin is a screen that quietly knows something. If three
 * things are withheld on a Sunday and nothing says so, the household has learnt
 * that the rail is unreliable — and a rail nobody believes is worse than a rail
 * that nags. So every screen that holds items states the count, and offers to show
 * them anyway. It is a default, not a lock.
 *
 * ## What never holds
 *
 * Urgent items, overdue items, and anything due before Monday. A gate left open, a
 * licence that expires on the Sunday — holding those until Monday would be the
 * feature actively causing the harm it was meant to avoid. "Non-urgent" is doing
 * real work in the spec's sentence and it is enforced here rather than assumed.
 */
import type { Attention } from "./estate.svelte";
import { addDays, houseISO, isSunday } from "./housetime";

/** Re-exported so a component asking "is it Sunday?" imports F11, not a date file. */
export { isSunday };

/**
 * Would this item wait until Monday?
 *
 * Note what is *not* consulted: `digestCount`. An item that has been in three
 * morning digests running is nagging precisely because nobody has done it, and
 * being nagged is not the same as being urgent — Sunday is exactly the day that
 * kind of item should stop shouting.
 */
export function holdsUntilMonday(a: Attention, now: Date | number = Date.now()): boolean {
  if (a.overdue || a.severity === "urgent") return false;
  if (a.dueAt === null) return true;

  // Due today, or before the week turns over? Then it is not a Monday problem.
  const monday = addDays(houseISO(now), 1);
  return houseISO(new Date(a.dueAt)) >= monday;
}

/**
 * Split the rail into what is shown and what waits.
 *
 * Returns both halves rather than a filtered list, because the count of the held
 * half is what the screen has to say out loud. A function that returned only the
 * survivors would make the honest sentence impossible to write.
 */
export function splitForSunday(
  items: Attention[],
  now: Date | number = Date.now(),
): { show: Attention[]; held: Attention[] } {
  if (!isSunday(now)) return { show: items, held: [] };
  const held = items.filter((a) => holdsUntilMonday(a, now));
  const heldIds = new Set(held.map((a) => a.id));
  return { show: items.filter((a) => !heldIds.has(a.id)), held };
}

/**
 * "Sunday. Three notes are holding until Monday."
 *
 * Written here rather than in each component so the tablet and the phone say the
 * same thing — two surfaces phrasing the same rule differently is how a household
 * concludes that one of them is broken.
 */
export function heldSentence(n: number): string {
  if (n === 0) return "Sunday. Nothing is waiting, and nothing is being held back.";
  return n === 1
    ? "Sunday. One note is holding until Monday."
    : `Sunday. ${n} notes are holding until Monday.`;
}

class SundayPrefs {
  /**
   * Set when somebody asks to see the held items anyway.
   *
   * Deliberately not persisted. It is a decision about this minute — "what was it
   * you were not telling me" — and a preference that survived to next Sunday would
   * silently switch the feature off for good.
   */
  showHeld = $state(false);
}

export const sunday = new SundayPrefs();
